import React, { useState } from "react"
import { Redirect, Route, Switch } from "react-router-dom"
import { getHasRequestCardInformationPage } from "derivedSelectors"
import { getHasDeductibleRemaining } from "derivedSelectors/deductibleSelectors"
import { useSelector } from "react-redux"
import { getCardState, getIsUsa, getOnCarrotCardNonQmeJourney } from "reducers/userInfo"
import { VerifyPhoneNumber } from "components/views/requestcard/VerifyPhoneNumber"
import { RequestCardInformation } from "components/views/requestcard/RequestCardInformation"
import { RequestCardMailingAddress } from "components/views/requestcard/RequestCardMailingAddress"
import { ConfirmPhoneNumber } from "components/views/requestcard/ConfirmPhoneNumber"
import { StripeUserAgreement } from "components/views/requestcard/StripeUserAgreement"
import CardStates from "utils/CardStates"
import { CountryCode } from "content/CountryMap"
import { useBenefitEnrollment } from "../../context/enrollment/BenefitEnrollmentContext"

type RequestCardContext = {
  phoneNumber?: string
  countryCode?: CountryCode
  completeConfirmPhoneNumber?: (phoneNumber: string, countryCode: CountryCode, verificationCode?: string) => void
  verificationCode?: string
}

export const RequestCardContext = React.createContext<RequestCardContext>({})

type RequestCardContainerState = {
  phoneNumber?: string
  countryCode?: CountryCode
  verificationCode?: string
}

const RequestCardContainer = () => {
  const [state, setState] = useState<RequestCardContainerState>({})
  const cardState = useSelector(getCardState)
  const hasRequestCardInformationPage = useSelector(getHasRequestCardInformationPage)
  const isUsa = useSelector(getIsUsa)
  const hasDeductibleRemaining = useSelector(getHasDeductibleRemaining)
  const onCarrotCardNonQmeJourney = useSelector(getOnCarrotCardNonQmeJourney)
  const requestCardPath = "/request-card"
  const informationPath = requestCardPath + "/information"
  const mailingAddressPath = requestCardPath + "/mailing-address"
  const phoneNumberPath = requestCardPath + "/phone-number"
  const phoneVerificationPath = requestCardPath + "/phone-verification"
  const stripeUserAgreementPath = requestCardPath + "/user-agreement"
  const { needsBenefitEnrollment } = useBenefitEnrollment()
  const completeConfirmPhoneNumber = (phoneNumber: string, countryCode: CountryCode, verificationCode: string) => {
    setState({ phoneNumber, countryCode, verificationCode })
  }

  const context = {
    completeConfirmPhoneNumber: completeConfirmPhoneNumber,
    phoneNumber: state.phoneNumber,
    countryCode: state.countryCode,
    verificationCode: state.verificationCode
  }

  return (
    <RequestCardContext.Provider value={context}>
      <Switch>
        {(cardState !== CardStates.ELIGIBLE ||
          needsBenefitEnrollment ||
          (hasDeductibleRemaining && !onCarrotCardNonQmeJourney)) && <Redirect to="/transactions" />}
        <Route exact path={requestCardPath}>
          {hasRequestCardInformationPage ? (
            <Redirect to={informationPath} />
          ) : isUsa ? (
            <Redirect to={mailingAddressPath} />
          ) : (
            <Redirect to={phoneNumberPath} />
          )}
        </Route>
        <Route exact path={informationPath}>
          <RequestCardInformation requestCardPath={requestCardPath} />
        </Route>
        <Route exact path={mailingAddressPath}>
          <RequestCardMailingAddress requestCardPath={requestCardPath} />
          {!isUsa && <Redirect to={phoneNumberPath} />}
        </Route>
        <Route exact path={phoneNumberPath}>
          <ConfirmPhoneNumber requestCardPath={requestCardPath} />
        </Route>
        <Route exact path={phoneVerificationPath}>
          <VerifyPhoneNumber requestCardPath={requestCardPath} />
        </Route>
        <Route exact path={stripeUserAgreementPath}>
          <StripeUserAgreement requestCardPath={requestCardPath} />
        </Route>
      </Switch>
    </RequestCardContext.Provider>
  )
}

export { RequestCardContainer }
